/**
 * The side menu of an entity page: Find a fact, then Overview and one topic per group the page has
 * rows for (in sh:order), then the data views: All facts (the plain table), Connected to and the
 * triples. It is a filter: the hero and the facts show what is picked. On a phone it is a select
 * above the hero.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@bn-apps/dim-ui-react';
import { Description, GridOn, Link, List } from '@bn-apps/design-system-icons';
import { DomainMark, topicOf, type FactGroup } from './Facts';
import styles from '../EntityTemplate.module.scss';

interface TopicMenuProps {
  groups: FactGroup[];
  topic: string;
  onTopic: (topic: string) => void;
  query: string;
  onQuery: (query: string) => void;
}

const DATA_VIEWS = [
  { id: 'all', icon: Description },
  { id: 'connected', icon: Link },
  { id: 'triples', icon: List },
] as const;

const TopicMenu = ({ groups, topic, onTopic, query, onQuery }: TopicMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const topics = groups.filter((group) => group.id !== 'other');
  const item = (id: string, title: string, mark: JSX.Element) => (
    <li key={id}>
      <button type="button" className={styles.topicItem} aria-current={topic === id ? 'page' : undefined} onClick={() => onTopic(id)}>
        {mark}
        <span className={styles.topicName}>{title}</span>
      </button>
    </li>
  );
  const icon = (Icon: typeof GridOn) => (
    <span className={styles.domainMark} style={{ '--domain': 'var(--dim-ink-secondary)' } as CSSProperties} aria-hidden="true">
      <Icon width={16} height={16} />
    </span>
  );
  return (
    <nav className={styles.topicMenu} aria-label={t('entity.template.topics.label')}>
      <SearchInput
        size="sm"
        className={styles.topicFind}
        aria-label={t('entity.template.facts.find')}
        placeholder={t('entity.template.facts.find')}
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        onKeyDown={(event) => event.key === 'Escape' && onQuery('')}
      />
      <label className={styles.topicSelect}>
        <span className={styles.visuallyHidden}>{t('entity.template.topics.label')}</span>
        <select value={topic} onChange={(event) => onTopic(event.target.value)}>
          <option value="overview">{t('entity.template.topics.overview')}</option>
          {topics.map((group) => (
            <option key={group.id} value={topicOf(group)}>
              {group.title}
            </option>
          ))}
          {DATA_VIEWS.map((view) => (
            <option key={view.id} value={view.id}>
              {t(`entity.template.topics.${view.id}`)}
            </option>
          ))}
        </select>
      </label>
      <ul className={styles.topicList}>
        {item('overview', t('entity.template.topics.overview'), icon(GridOn))}
        {topics.map((group) => item(topicOf(group), group.title, <DomainMark domain={group.domain} raw={group.raw} />))}
      </ul>
      <h3 className={styles.topicGroupTitle}>{t('entity.template.topics.data')}</h3>
      <ul className={styles.topicList}>
        {DATA_VIEWS.map((view) => item(view.id, t(`entity.template.topics.${view.id}`), icon(view.icon)))}
      </ul>
    </nav>
  );
};

export default TopicMenu;
